import React, { useState, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { formatDisplayDateTime } from '../../../utils/dateTimeFormat';

export const systemParameterLabelMap: { [key: string]: string } = {
    '11': 'Modem Status',
    '30': 'CPU Utilization',
    '31': 'Memory Utilization',
    '32': 'Internal Disk Utilization',
    '33': 'External Disk Utilization',
    '34': 'LTE Modem Signal Strength',
    '35': 'LTE Network Total RX Bytes',
    '36': 'LTE Network Total TX Bytes',
    '37': 'Eth0 Network Total RX Bytes',
    '38': 'Eth0 Network Total TX Bytes',
    '39': 'LTE Network RX Bytes/min',
    '40': 'LTE Network TX Bytes/min',
    '41': 'Eth0 Network RX Bytes/min',
    '42': 'Eth0 Network TX Bytes/min',
    '43': 'Last Day Eth0 Total RX',
    '44': 'Last Day Eth0 Total TX',
    '45': 'Last Day LTE Total RX Bytes',
    '46': 'Last Day LTE Total TX Bytes',
    '47': 'Signal Quality',
    '49': 'Latitude',
    '50': 'Longitude',
    '51': 'FTP Pending Files Count',
    '52': 'MQTT Pending Files Count',
    '53': 'Daily Modem Online Count',
    '54': 'Last Day Modem Online Count',
    '55': 'Daily Modem Online Time',
    '56': 'Last Day Modem Online Time',
    '57': 'RTU StartUp Time',
    '58': 'RTU Current Time',
    '59': 'Total Backup FTP TX Count',
    '60': 'Last Day Backup FTP TX Count',
    '61': 'Last FTP Transmit Time',
    '62': 'Last MQTT Transmit Time',
    '63': 'Last MQTT Transmit Time',
    '65': 'Last Backup FTP Transmit Time',
    '66': 'VPN Status',
    '100': 'Reboot Count',
    '101': 'Power Off Count',
    '102': 'Total Restart Count',
    '103': 'Last Day Reboot Count',
    '104': 'Last Day Power Off Count',
    '105': 'Last Day Restart Count',
    '106': 'Total Login Count',
    '107': 'Last Day Login Count',
    '108': 'Total FTP TX Count',
    '109': 'Last Day FTP TX Count',
    '110': 'Total MQTT TX Count',
    '111': 'Last Day MQTT TX Count',
};

const isTimestampField = (key: string) => {
    const timestampFields = ['57', '58', '61', '62', '63', '65'];
    return timestampFields.includes(key);
};

const isDurationField = (key: string) => {
    return key === '55' || key === '56';
};

const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
};

const isLocationField = (key: string) => {
    return key === '49' || key === '50';
};

export const getSystemParameterLabel = (key: string) => systemParameterLabelMap[key] || key;

const formatComplexValue = (value: any): string => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value.map((entry) => formatComplexValue(entry)).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
        const totalAsNumber =
            typeof value.total === 'number'
                ? value.total
                : typeof value.total === 'string' && value.total.trim() && !Number.isNaN(Number(value.total))
                    ? Number(value.total)
                    : null;
        const dataEntriesCount =
            value.dataEntries && typeof value.dataEntries === 'object'
                ? Object.keys(value.dataEntries).length
                : null;
        const entriesCount =
            value.entries && typeof value.entries === 'object'
                ? Object.keys(value.entries).length
                : null;
        const count = totalAsNumber ?? dataEntriesCount ?? entriesCount;
        if (count !== null && count !== undefined) {
            return `${count} items`;
        }

        if (typeof value.status === 'string' && value.status.trim()) {
            return value.status;
        }

        if (typeof value.config === 'string' && value.config.trim()) {
            return value.config;
        }

        try {
            return JSON.stringify(value);
        } catch {
            return '[object]';
        }
    }

    return String(value);
};

export const formatSystemParameterValue = (key: string, value: any) => {
    const tagName = getSystemParameterLabel(key);
    if (isDurationField(key)) {
        const seconds = Number(value);
        return Number.isFinite(seconds) ? formatDuration(seconds) : formatComplexValue(value);
    }
    if (isTimestampField(key)) {
        const timestamp = Number(value);
        return value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(timestamp) && timestamp > 0
            ? formatDisplayDateTime(timestamp)
            : '—';
    }
    if (!isNaN(value)) {
        if (isLocationField(key)) {
            return value;
        }
        let formattedValue = Number(value).toFixed(tagName.includes('Utilization') ? 1 : 0);
        if (tagName.includes('Utilization')) {
            formattedValue += '%';
        }
        return formattedValue;
    }
    return formatComplexValue(value);
};

const SYSTEM_PARAMETER_PRIORITY_KEYS = ['30', '31', '32', '33', '34', '47', '51', '52', '57', '58', '100', '106'];

const SystemParameters = ({ systemData, expandContent = false }: { systemData: any; expandContent?: boolean }) => {
    const { t } = useTranslation();
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const entries = useMemo(() => {
        if (!systemData) return [];
        const priority = new Map<string, number>(
            SYSTEM_PARAMETER_PRIORITY_KEYS.map((key, index): [string, number] => [key, index])
        );
        return Object.entries(systemData).sort(([left], [right]) => {
            const leftPriority = priority.get(left);
            const rightPriority = priority.get(right);
            if (leftPriority !== undefined || rightPriority !== undefined) {
                return (leftPriority ?? Number.MAX_SAFE_INTEGER) - (rightPriority ?? Number.MAX_SAFE_INTEGER);
            }
            return getSystemParameterLabel(left).localeCompare(getSystemParameterLabel(right));
        });
    }, [systemData]);

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return entries;
        const term = searchTerm.toLowerCase();
        return entries.filter(([key, value]) => {
            const label = getSystemParameterLabel(key);
            const formattedValue = String(formatSystemParameterValue(key, value));
            return label.toLowerCase().includes(term) || formattedValue.toLowerCase().includes(term);
        });
    }, [searchTerm, entries]);

    if (!systemData) {
        return null;
    }

    const cardTitle = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('rtuDetail.sections.systemParameters')}</span>
            <Button
                icon="pi pi-search"
                className="p-button-text p-button-sm"
                onClick={() => setShowSearch(!showSearch)}
                tooltip={showSearch ? t('search.hide') : t('search.show')}
                tooltipOptions={{ position: 'left' }}
                style={{ padding: '0.15rem 0.3rem', width: 'auto', height: 'auto', color: '#333' }}
            />
        </div>
    );

    return (
        <Card
            title={cardTitle}
            className={`info-card compact-card flat-card${expandContent ? ' system-metrics-card' : ''}`}
        >
            {showSearch && (
                <div style={{ marginBottom: '0.25rem' }}>
                    <InputText
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('search.filter')}
                        style={{ width: '100%', fontSize: '0.75rem', height: '1.4rem', padding: '0.125rem 0.25rem' }}
                    />
                </div>
            )}
            <div className={expandContent ? 'system-metrics-card__content' : 'scrollable-card-content'}>
                <table className="kv-table">
                    <tbody>
                        {filteredEntries.map(([key, value]) => (
                            <tr key={key}>
                                <td>{getSystemParameterLabel(key)}</td>
                                <td>{formatSystemParameterValue(key, value)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

export default SystemParameters;
